import {
  type JSX,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { useFloatingPortalContainer, useIslandHost } from "@frontend/contexts";

import { Drawer } from "./Drawer";
import { type DrawerDataProps, drawerDataSchema } from "./Drawer.schema";
import type { DrawerProps } from "./Drawer.types";
import { DrawerClose } from "./DrawerClose";
import { DrawerContent } from "./DrawerContent";
import { DrawerHeader } from "./DrawerHeader";
import { DrawerPopup } from "./DrawerPopup";
import { DrawerTitle } from "./DrawerTitle";
import { DRAWER_TAG_NAME } from "./constants";

type DrawerComponentProps = Partial<DrawerDataProps> & {
  ref?: Ref<DrawerHandle>;
};

export interface DrawerHandle {
  open: () => void;
  close: () => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [DRAWER_TAG_NAME]: {
      props: DrawerComponentProps;
      handle: DrawerHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-drawer": HTMLElement & DrawerHandle;
  }
}

function RWCDrawerComponent({
  ref,
  title,
  size,
  border,
  swipeDirection,
  modal,
  disablePointerDismissal,
}: DrawerComponentProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const floatingPortalRoot = useFloatingPortalContainer();
  const host = useIslandHost();

  const wasNestedPopupOpenAtPressTime = useRef(false);

  useImperativeHandle(
    ref,
    () => ({
      open: () => setOpen(true),
      close: () => setOpen(false),
    }),
    [],
  );

  // The drawer also closes on Escape, a swipe or an outside press, which the
  // page never sees otherwise.
  const announcedOpen = useRef(open);

  useEffect(() => {
    if (announcedOpen.current === open) {
      return;
    }

    announcedOpen.current = open;

    host?.dispatchEvent(
      new CustomEvent(open ? "drawer:open" : "drawer:close", {
        bubbles: true,
      }),
    );
  }, [open, host]);

  // Prevents nested islands' popups
  useEffect(() => {
    if (!floatingPortalRoot) {
      return;
    }

    const portalRoot = floatingPortalRoot;

    function handlePointerDown() {
      wasNestedPopupOpenAtPressTime.current =
        portalRoot.querySelector("[data-open]") != null;
    }

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("mousedown", handlePointerDown, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("mousedown", handlePointerDown, true);
    };
  }, [floatingPortalRoot]);

  const handleOpenChange = useCallback<
    NonNullable<DrawerProps["onOpenChange"]>
  >(
    (nextOpen, eventDetails) => {
      const isDismissingBecauseOfNestedPopup =
        !nextOpen &&
        eventDetails.reason === "outside-press" &&
        (floatingPortalRoot?.querySelector("[data-open]") != null ||
          wasNestedPopupOpenAtPressTime.current);

      if (isDismissingBecauseOfNestedPopup) {
        eventDetails.cancel();
        return;
      }

      setOpen(nextOpen);
    },
    [floatingPortalRoot],
  );

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
      swipeDirection={swipeDirection}
      modal={modal}
      disablePointerDismissal={disablePointerDismissal}
    >
      <DrawerPopup size={size} border={border} backdrop={modal !== false}>
        <DrawerHeader>
          <DrawerTitle>{title}</DrawerTitle>
          <DrawerClose />
        </DrawerHeader>

        <DrawerContent>
          <slot />
        </DrawerContent>
      </DrawerPopup>
    </Drawer>
  );
}

componentManager.register(
  rwc(DRAWER_TAG_NAME, {
    component: RWCDrawerComponent,
    dataSchema: drawerDataSchema,
    usesSlots: true,
  }),
);
